import { ApolloLink, type Operation, type NextLink, type FetchResult } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import type { ErrorResponse } from '@apollo/client/link/error';
import { LogCategory, generateCorrelationId, type BrowserLogger } from '@soteria/logger/browser';

/**
 * Sensitive field patterns to redact from GraphQL variables
 */
const SENSITIVE_FIELD_PATTERNS = [/password/i, /token/i, /secret/i, /authorization/i, /auth/i, /key/i, /credential/i, /session/i];

/**
 * Network error interface for type safety
 */
interface NetworkErrorWithResponse {
  statusCode?: number;
  response?: {
    status: number;
    statusText: string;
  };
}

/**
 * Sanitizes GraphQL variables by redacting sensitive data
 * @param variables The variables object to sanitize
 * @returns Sanitized variables with sensitive fields redacted
 */
export function sanitizeVariables(variables: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!variables || typeof variables !== 'object') {
    return variables;
  }

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(variables)) {
    // Check if the key matches any sensitive pattern
    const isSensitive = SENSITIVE_FIELD_PATTERNS.some(pattern => pattern.test(key));

    if (isSensitive) {
      sanitized[key] = '[REDACTED]';
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeVariables(value as Record<string, unknown>);
    } else if (Array.isArray(value)) {
      // Handle arrays by sanitizing each object element
      sanitized[key] = value.map(item => (item && typeof item === 'object' ? sanitizeVariables(item as Record<string, unknown>) : item));
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Logs operation completion with performance metrics
 */
function calculateOperationMetrics(startTime: number, result: FetchResult) {
  const endTime = performance.now();
  const duration = Math.round(endTime - startTime);
  const dataSize = result.data ? JSON.stringify(result.data).length : 0;
  const hasErrors = result.errors && result.errors.length > 0;
  const hasPartialFailures = result.errors?.some(error => error.extensions?.partialFailure === true);

  return { duration, dataSize, hasErrors, hasPartialFailures };
}

function logSuccessfulOperation(
  logger: BrowserLogger,
  context: { operation: Operation; correlationId: string; metrics: ReturnType<typeof calculateOperationMetrics> }
): void {
  const { operation, correlationId, metrics } = context;
  const { hasPartialFailures, duration, dataSize } = metrics;

  const operationName = operation.operationName || 'unnamed';

  // Log ALL GraphQL operations at debug level for comprehensive tracking
  logger.debug('GraphQL operation completed', {
    category: LogCategory.API,
    correlationId,
    operation: operationName,
    hasPartialFailures,
    duration,
    dataSize,
    timestamp: new Date().toISOString(),
  });
}

function logCorrelationIdUpdate(logger: BrowserLogger, context: { operation: Operation; originalId: string; responseId: string }): void {
  const { operation, originalId, responseId } = context;

  logger.debug('Correlation ID updated from response', {
    category: LogCategory.SYSTEM,
    originalCorrelationId: originalId,
    responseCorrelationId: responseId,
    operation: operation.operationName || 'unnamed',
  });
}

function logOperationCompletion(logger: BrowserLogger, context: { operation: Operation; correlationId: string; startTime: number; result: FetchResult }): void {
  const { operation, correlationId, startTime, result } = context;
  const metrics = calculateOperationMetrics(startTime, result);

  // Log successful operation completion
  if (!metrics.hasErrors || metrics.hasPartialFailures) {
    logSuccessfulOperation(logger, { operation, correlationId, metrics });
  }

  // Extract and propagate correlation ID from response headers if available
  const responseCorrelationId = result.extensions?.correlationId;

  if (responseCorrelationId && responseCorrelationId !== correlationId) {
    logCorrelationIdUpdate(logger, { operation, originalId: correlationId, responseId: responseCorrelationId });
  }
}

/**
 * Creates an Apollo Link that adds correlation IDs to requests and logs operations
 * @returns Apollo Link for correlation ID management and operation logging
 */
export function createLoggingCorrelationIdLink(): ApolloLink {
  return new ApolloLink((operation: Operation, forward: NextLink) => {
    const startTime = performance.now();

    // Get correlation ID from context or generate a new one
    const context = operation.getContext();
    const correlationId = context.correlationId || generateCorrelationId();

    // Add correlation ID to headers and context
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'X-Correlation-ID': correlationId,
      },
      correlationId,
    }));

    // Get logger instance (this will be available through React context)
    const logger = context.logger;

    if (logger) {
      const operationName = operation.operationName || 'unnamed';

      // Log ALL GraphQL operations at debug level for comprehensive tracking
      logger.debug('GraphQL operation started', {
        category: LogCategory.API,
        correlationId,
        operation: operationName,
        operationType: operation.query.definitions[0]?.kind === 'OperationDefinition' ? operation.query.definitions[0].operation : 'unknown',
        variables: sanitizeVariables(operation.variables),
        timestamp: new Date().toISOString(),
      });
    }

    return forward(operation).map((result: FetchResult) => {
      if (logger) {
        logOperationCompletion(logger, { operation, correlationId, startTime, result });
      }

      return result;
    });
  });
}

/**
 * Logs GraphQL errors with detailed context
 */
interface GraphQLErrorLike {
  message: string;
  locations?: readonly { line: number; column: number }[];
  path?: readonly (string | number)[];
  extensions?: Record<string, unknown>;
}

function formatGraphQLErrors(errors: readonly unknown[]): GraphQLErrorLike[] {
  return errors.map(error => {
    const graphqlError = error as GraphQLErrorLike;

    return {
      message: graphqlError.message || 'Unknown GraphQL error',
      locations: graphqlError.locations,
      path: graphqlError.path,
      extensions: graphqlError.extensions,
    };
  });
}

function logGraphQLErrors(
  logger: BrowserLogger,
  context: { errors: readonly unknown[]; operation: Operation; correlationId: string; errorType: 'graphql_error' | 'partial_failure'; response?: unknown }
): void {
  const { errors, operation, correlationId, errorType, response } = context;
  const logLevel = errorType === 'partial_failure' ? 'warn' : 'error';
  const message = errorType === 'partial_failure' ? 'GraphQL operation completed with partial failures' : 'GraphQL operation failed with errors';

  logger[logLevel](message, {
    category: LogCategory.API,
    correlationId,
    operation: operation.operationName || 'unnamed',
    [errorType === 'partial_failure' ? 'partialFailures' : 'errors']: formatGraphQLErrors(errors),
    variables: sanitizeVariables(operation.variables),
    timestamp: new Date().toISOString(),
    errorType,
    [errorType === 'partial_failure' ? 'failureCount' : 'errorCount']: errors.length,
    ...(errorType === 'partial_failure' && { hasSuccessfulData: !!response }),
  });
}

function handleGraphQLErrors(
  logger: BrowserLogger,
  context: { graphQLErrors: readonly unknown[]; operation: Operation; correlationId: string; response?: unknown }
): void {
  const { graphQLErrors, operation, correlationId, response } = context;

  // Separate regular errors from partial failures
  const partialFailures = graphQLErrors.filter(error => (error as GraphQLErrorLike).extensions?.partialFailure === true);
  const regularErrors = graphQLErrors.filter(error => !(error as GraphQLErrorLike).extensions?.partialFailure);

  // Log regular GraphQL errors
  if (regularErrors.length > 0) {
    logGraphQLErrors(logger, { errors: regularErrors, operation, correlationId, errorType: 'graphql_error' });
  }

  // Log partial failures with different severity
  if (partialFailures.length > 0) {
    logGraphQLErrors(logger, { errors: partialFailures, operation, correlationId, errorType: 'partial_failure', response });
  }
}

function handleNetworkError(logger: BrowserLogger, context: { networkError: Error; operation: Operation; correlationId: string }): void {
  const { networkError, operation, correlationId } = context;
  const isTimeoutError = networkError.message?.includes('timeout') || networkError.name === 'TimeoutError';
  const isConnectionError = networkError.message?.includes('fetch') || networkError.message?.includes('network');
  const networkErrorWithResponse = networkError as NetworkErrorWithResponse;

  logger.error('GraphQL network error occurred', {
    category: LogCategory.API,
    correlationId,
    operation: operation.operationName || 'unnamed',
    error: {
      name: networkError.name,
      message: networkError.message,
      stack: networkError.stack,
    },
    variables: sanitizeVariables(operation.variables),
    timestamp: new Date().toISOString(),
    errorType: 'network_error',
    networkErrorType: isTimeoutError ? 'timeout' : isConnectionError ? 'connection' : 'unknown',
    statusCode: networkErrorWithResponse.statusCode,
    response: networkErrorWithResponse.response
      ? {
          status: networkErrorWithResponse.response.status,
          statusText: networkErrorWithResponse.response.statusText,
        }
      : undefined,
  });
}

function processErrorResponse(errorResponse: ErrorResponse): void {
  const { graphQLErrors, networkError, operation, response } = errorResponse;

  // Extract correlation ID from operation context
  const context = operation.getContext();
  const correlationId = context.correlationId || context.headers?.['X-Correlation-ID'];
  const logger = context.logger;

  if (!logger) {
    // Fallback to console logging if logger is not available
    console.error('Apollo Error Link: Logger not available in context');

    return;
  }

  // Handle GraphQL errors
  if (graphQLErrors && graphQLErrors.length > 0) {
    handleGraphQLErrors(logger, { graphQLErrors, operation, correlationId, response });
  }

  // Handle network errors
  if (networkError) {
    handleNetworkError(logger, { networkError, operation, correlationId });
  }
}

/**
 * Creates an enhanced error link with structured logging for GraphQL and network errors
 * @returns Apollo Link for comprehensive error logging
 */
export function createLoggingErrorLink(): ApolloLink {
  return onError(processErrorResponse);
}
